import React, { useEffect, useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  getBasicInfoQuestions,
  domicileStateQuestion,
  stateOfResidenceQuestion,
  exemptionAndWithholdingQuestions,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version2025({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  version,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  // Basic Information Questions + Marital Status
  const basicInfoForm = useRef();
  const basicInfoQuestions = getBasicInfoQuestions(version);
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  // Exemption and Additional Withholding Question - Line 1 and 2
  const exemptionAndWithholdingForm = useRef();
  const exemptionAndWithholdingInitQ = getInitialQuestions(exemptionAndWithholdingQuestions);
  // eslint-disable-next-line max-len
  const [exemptionAndWithholdingQ, setExemptionAndWithholdingQ] = useState(exemptionAndWithholdingInitQ);

  // Exemption Questions
  const exemptionForm = useRef();
  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(data.is_exempt || false);

  // Q3a
  const [noMDIncomeTax, setNoMDIncomeTax] = useState(data.owes_no_MD_income_tax || false);
  // Q3b
  const [yearEffective, setYearEffective] = useState(data.year_applicable || '');
  const [yearEffectiveError, setYearEffectiveError] = useState(false);
  // Q4
  const [domiciled1, setDomiciled1] = useState(data.domiciled_in_DC_VA_WV || false);
  const domicileStateInitQ = getInitialQuestions(domicileStateQuestion);
  const [domicileState, setDomicileState] = useState(domicileStateInitQ);
  // Q5
  const [domiciled2, setDomiciled2] = useState(data.domiciled_in_PA || false);
  // Q6
  const [liveInPA1, setLiveInPA1] = useState(data.live_in_York_Adams_counties || false);
  // Q7
  const [liveInPA2, setLiveInPA2] = useState(data.live_in_PA_no_tax_on_MD_residents || false);
  // Q8
  const [serviceMember, setServiceMember] = useState(data.servicemembers_civil_relief_act || false);
  const stateOfResidenceInitQ = getInitialQuestions(stateOfResidenceQuestion);
  const [stateOfResidence, setStateOfResidence] = useState(stateOfResidenceInitQ);

  useEffect(() => {
    setBasicInfo(basicInfoInitQ);
  }, [version]);

  const renderBasicInfoForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderExemptionAndWithholdingForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={exemptionAndWithholdingQ}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = exemptionAndWithholdingQ.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setExemptionAndWithholdingQ(questions);
        }}
        ref={exemptionAndWithholdingForm}
      />
    </Flex>
  );

  const renderDomicileState = (isReviewStep) => (
    <FormConsumer
      questions={domicileState}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = domicileState.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setDomicileState(questions);
      }}
      ref={exemptionForm}
    />
  );

  const renderStateOfRes = (isReviewStep) => (
    <FormConsumer
      questions={stateOfResidence}
      {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
      onQuestionChange={({ question, value }) => {
        const questions = stateOfResidence.map((q) => {
          if (q.id !== question.id) return q;
          const answer = {
            ...q.answer,
            answer: {
              ...q.answer.answer,
              [q.id]: value,
            },
          };
          const updatedQuestion = { ...q, answer };
          return updatedQuestion;
        });
        setStateOfResidence(questions);
      }}
      ref={exemptionForm}
    />
  );

  const renderYearEffective = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel mb="7px" isRequired={noMDIncomeTax}>
        Enter the year applicable (year effective)
      </S.QuestionLabel>
      <S.QuestionWrapper>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={yearEffective}
            onChange={(e) => setYearEffective(e.target.value)}
            disabled={isReviewStep}
            style={{ borderColor: yearEffectiveError ? 'red' : '' }}
          />
        </Flex>
      </S.QuestionWrapper>
      {yearEffectiveError && (
      <Flex justifyContent="flex-start">
        <Text color="red">Please enter a valid year</Text>
      </Flex>
      )}
    </Flex>
  );

  const renderExemptions = (isReviewStep) => (
    <Flex column>
      <Text mb="10px" style={{ fontWeight: '600', fontSize: '15px' }}>
        Exemption from withholding
      </Text>
      <Flex column>
        <S.QuestionLabel mb="15px" mt="10px" isRequired>
          To claim exemption from withholding, see instructions on Maryland Form MW507,
          then check boxes here that apply.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label mb="10px">
            <Flex mt="10px" style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={noMDIncomeTax}
                checked={noMDIncomeTax}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setNoMDIncomeTax(!noMDIncomeTax);
                }}
              />
              <Box ml={1} />
              Last year I did not owe any Maryland income tax and had a right to a full refund of
              all income tax withheld AND this year I do not expect to owe any Maryland income tax
              and expect to have the right to a full refund of all income tax withheld.
              (This includes seasonal and student employees whose annual income will be below
              the minimum filing requirements).
            </Flex>
          </label>
          {noMDIncomeTax && renderYearEffective(isReviewStep)}
          <label mb="10px">
            <Flex style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={domiciled1}
                checked={domiciled1}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setDomiciled1(!domiciled1);
                }}
              />
              <Box ml={2} />
              I claim exemption from withholding because I am domiciled in the
              District of Columbia, Virginia, or West Virginia.
            </Flex>
          </label>
          {domiciled1 && renderDomicileState(isReviewStep)}
          <label mb="10px">
            <Flex style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={domiciled2}
                checked={domiciled2}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setDomiciled2(!domiciled2);
                }}
              />
              <Box ml={2} />
              I claim exemption from Maryland state withholding because I am domiciled in
              the Commonwealth of Pennsylvania and I do not maintain a place of abode in
              Maryland as described in the instructions on Form MW507.
            </Flex>
          </label>
          <label mb="10px">
            <Flex style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={liveInPA1}
                checked={liveInPA1}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setLiveInPA1(!liveInPA1);
                }}
              />
              <Box ml={2} />
              I claim exemption from Maryland local tax because I live in a local Pennsylvania
              jurisdiction within York or Adams counties.
            </Flex>
          </label>
          <label mb="10px">
            <Flex style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={liveInPA2}
                checked={liveInPA2}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setLiveInPA2(!liveInPA2);
                }}
              />
              <Box ml={2} />
              I claim exemption from Maryland local tax because I live in a local Pennsylvania
              jurisdiction that does not impose an earnings or income tax on Maryland resident
            </Flex>
          </label>
          <label mb="10px">
            <Flex style={{ marginBottom: '15px' }}>
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={serviceMember}
                checked={serviceMember}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setServiceMember(!serviceMember);
                }}
              />
              <Box ml={2} />
              I am not subject to Maryland withholding because I meet the requirements set forth
              under the Servicemembers Civil Relief Act, as amended by the Military Spouses
              Residency Relief Act.
            </Flex>
          </label>
          {serviceMember && renderStateOfRes(isReviewStep)}
        </S.QuestionWrapper>
      </Flex>
    </Flex>
  );

  const handleValidation = () => {
    if (basicInfoForm && basicInfoForm.current
      && basicInfoForm.current.validate((error) => !!error)) {
      return true;
    }

    if (exemptionForm && exemptionForm.current
      && exemptionForm.current.validate((error) => !!error)) {
      return true;
    }

    if (!isExemptFromWithholding) {
      if (exemptionAndWithholdingForm && exemptionAndWithholdingForm.current
        && exemptionAndWithholdingForm.current.validate((error) => !!error)) {
        return true;
      }
    } else if (noMDIncomeTax && !/^\d{4}$/.test(yearEffective)) {
      setYearEffectiveError(true);
      return true;
    }

    setYearEffectiveError(false);
    return false;
  };

  const handleSubmission = () => {
    const hasErrors = handleValidation();

    if (hasErrors) return;

    const basicInfoData = basicInfo
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    const exemptionStatusData = { is_exempt: isExemptFromWithholding };

    // Initial data
    let noMDIncomeTaxTemp = '';
    let domiciled1Temp = '';
    let domiciled2Temp = '';
    let liveInPA1Temp = '';
    let liveInPA2Temp = '';
    let serviceMemberTemp = '';
    let yearEffectiveTemp = '';

    let domicileStateData = resetInitialQuestions(domicileStateQuestion)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let stateOfResData = resetInitialQuestions(stateOfResidenceQuestion)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let exemptionAndWithholdingData = resetInitialQuestions(exemptionAndWithholdingQuestions)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    if (isExemptFromWithholding) {
      noMDIncomeTaxTemp = noMDIncomeTax;
      domiciled1Temp = domiciled1;
      domiciled2Temp = domiciled2;
      liveInPA1Temp = liveInPA1;
      liveInPA2Temp = liveInPA2;
      serviceMemberTemp = serviceMember;

      if (domiciled1) {
        domicileStateData = domicileState
          .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
      }
      if (serviceMember) {
        stateOfResData = stateOfResidence
          .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
      }
      if (noMDIncomeTax) {
        yearEffectiveTemp = yearEffective;
      }
    } else {
      exemptionAndWithholdingData = exemptionAndWithholdingQ
        .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    }

    const exemptionSelectData = {
      owes_no_MD_income_tax: noMDIncomeTaxTemp,
      domiciled_in_DC_VA_WV: domiciled1Temp,
      domiciled_in_PA: domiciled2Temp,
      live_in_York_Adams_counties: liveInPA1Temp,
      live_in_PA_no_tax_on_MD_residents: liveInPA2Temp,
      servicemembers_civil_relief_act: serviceMemberTemp,
      year_applicable: yearEffectiveTemp,
    };
    const allExemptionData = {
      ...exemptionStatusData,
      ...exemptionSelectData,
      ...domicileStateData,
      ...stateOfResData,
    };

    const payload = {
      ...basicInfoData,
      ...allExemptionData,
      ...exemptionAndWithholdingData,
    };
    saveFormData(payload, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {isExemptFromWithholding && renderExemptions()}
          {!isExemptFromWithholding && renderExemptionAndWithholdingForm()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={handleSubmission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {isExemptFromWithholding && renderExemptions(true)}
        {!isExemptFromWithholding && renderExemptionAndWithholdingForm(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>

  );
}

export default Version2025;
